import { Prevision } from '../pipeline/Prevision.js';
import { BlocAPrevenir } from '../profils/BlocAPrevenir.js';
import { texteEnLangue } from '@artosera/shared';
import { Badge, Button, classesBouton } from '@artosera/ui';
import { formatDate } from '@artosera/shared';
import {
  ArrowUpRight,
  CalendarRange,
  Contact,
  Globe,
  Image as ImageIcon,
  Lock,
  SlidersHorizontal,
} from 'lucide-react';
import { useEffect, useRef, useState, type CSSProperties, type JSX, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router';
import { useTableauDeBord } from '../../api/tableauDeBord.js';
import { usePrevision } from '../../api/pipeline.js';
import { useTableauProfils } from '../../api/profils.js';
import { usePublierSite } from '../../api/site.js';
import { useOeuvre } from '../../api/oeuvres.js';
import { useApercuMedia } from '../../api/medias.js';
import { estApiError } from '../../api/errors.js';
import { useShellContext } from '../shell/context.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { teinteDeLEtat } from '../expositions/etatUi.js';
import { teinteDeLEcheance } from '../coffre/echeanceUi.js';
import { BientotSection } from '../modules/BientotSection.js';
import { CarteDesModules } from '../modules/CarteDesModules.js';
import {
  filtreEffectif,
  modulesAVenirDe,
  palierDuModule,
  type FiltreCarte,
  type PalierAVenir,
} from '../modules/modulesAVenir.js';
import { BlocsReorganisables, type BlocAffiche } from './BlocsReorganisables.js';
import { appliquerOrdreVisible, useOrdreModules, type IdModule } from './ordreModules.js';
import styles from './TableauDeBordPage.module.css';

interface EnteteSuivi {
  readonly titre: ReactNode;
  readonly lien?: { readonly to: string; readonly libelle: string };
}

function Suivi({ entete, children }: { entete: EnteteSuivi; children: ReactNode }): JSX.Element {
  return (
    <section className={styles.suivi} data-ui="dashboard-followup">
      <div className={styles.suiviEntete}>
        <h2>{entete.titre}</h2>
        {entete.lien ? (
          <Link className={classesBouton('secondary', 'sm')} to={entete.lien.to}>
            {entete.lien.libelle}
          </Link>
        ) : null}
      </div>
      {children}
    </section>
  );
}

/**
 * Tableau de bord (3.2, point 1, ARTO-18, ARTO-45, ARTO-53) : reprend la
 * composition Halo validée — une priorité du jour dominante, l'œuvre en
 * vedette (avec son image), quatre indicateurs alignés, puis des suivis
 * secondaires — plutôt qu'une grille de cartes de poids égal (audit du
 * 23 septembre 2026, § 1).
 *
 * UN SEUL appel, `GET …/tableau-de-bord` (§ 20), nourrit toute la
 * composition. Une deuxième requête, ciblée sur un seul document
 * (`useOeuvre`, déjà utilisée par la fiche œuvre), va chercher l'image de
 * l'œuvre mise en avant — jamais une liste : c'est la même économie que
 * l'agrégation, appliquée à la vedette.
 *
 * Les tuiles restent gouvernées par le module et l'action côté client (6.4,
 * 5.4, même garde que `routes/shell/navigation.ts`) : la session les connaît
 * sans attendre le réseau. Le serveur applique exactement les mêmes gardes et
 * omet les sections fermées — un demandeur ne peut donc rien voir de plus que
 * sa tuile.
 */
export function TableauDeBordPage(): JSX.Element {
  const { t, i18n } = useTranslation(['regie', 'commun', 'oeuvres', 'ventes', 'site', 'coffre']);
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const navigate = useNavigate();
  const { session, galerie, galerieSlug, compte } = useShellContext();

  // Modules à venir : lus dans la session, jamais sur le réseau. Sans palier
  // verrouillé, ni section, ni case fantôme, ni carte.
  const avenir = modulesAVenirDe(session, galerie);
  // Le choix de la personne ; le filtre appliqué se recalcule dès que l'ensemble des
  // modules à venir change (un palier débloqué page ouverte).
  const [filtreChoisi, setFiltreCarte] = useState<FiltreCarte | null>(null);
  const filtreCarte = filtreEffectif(filtreChoisi, avenir);
  const titreCarte = useRef<HTMLHeadingElement>(null);
  const { hash } = useLocation();
  const allerALaCarte = (): void => {
    const carte = document.getElementById('carte-des-modules');
    carte?.scrollIntoView({ block: 'start' });
    titreCarte.current?.focus({ preventScroll: true });
  };
  useEffect(() => {
    if (hash === '#carte-des-modules' && !avenir.aucun) {
      document.getElementById('carte-des-modules')?.scrollIntoView({ block: 'start' });
    }
  }, [hash, avenir.aucun]);
  const voirSurLaCarte = (palier: PalierAVenir): void => {
    setFiltreCarte(palier);
    // La carte se redessine avant que le défilement la cherche.
    requestAnimationFrame(allerALaCarte);
  };

  const voitDemandes =
    galerieAModule(galerie, 'contacts') && galerieALaction(galerie, 'inquiries:read');
  const voitReservations = galerieALaction(galerie, 'reservations:read');
  const voitExpositions =
    galerieAModule(galerie, 'expositions') && galerieALaction(galerie, 'exhibitions:read');
  const voitOeuvres =
    galerieAModule(galerie, 'oeuvres') && galerieALaction(galerie, 'artworks:read');
  const voitSite = galerieAModule(galerie, 'site') && galerieALaction(galerie, 'site:read');
  const peutPublier = galerieALaction(galerie, 'site:publish');
  const voitEcheances =
    galerieAModule(galerie, 'coffre') && galerieALaction(galerie, 'documents:read');

  const tableau = useTableauDeBord(galerieSlug);
  const publier = usePublierSite(galerieSlug);
  const donneesPretes = !tableau.isLoading && !tableau.isError;

  // `new Date()` (plutôt que `Date.now()`) : l'en-tête date la journée, sans
  // déclencher la règle de pureté du rendu (react-hooks/purity, qui ne
  // surveille que `Date.now`). Les fenêtres, elles, sont désormais serveur.
  const aujourdhui = new Date();

  const donnees = tableau.data;
  const messageErreur = estApiError(tableau.error)
    ? tableau.error.message
    : t('regie:tableauDeBord.erreur');

  const demandes = donnees?.demandes;
  const reservations = donnees?.reservations;
  const expositions = donnees?.expositions;
  const oeuvresSection = donnees?.oeuvres;
  const coffre = donnees?.coffre;
  const derniere = donnees?.site?.derniere ?? null;

  // La priorité du jour : une réservation qui approche l'emporte sur la
  // prochaine demande, elle-même seule alternative avant l'état calme.
  const prochaineReservation =
    voitReservations && reservations && reservations.prochaines.length > 0
      ? reservations.prochaines[0]!
      : null;
  const prochaineDemande =
    voitDemandes && demandes && demandes.dernieres.length > 0 ? demandes.dernieres[0]! : null;
  const lienProchaineDemande = prochaineDemande
    ? `/${galerieSlug}/contacts?onglet=demandes&etat=a_traiter#demande-${encodeURIComponent(prochaineDemande.id)}`
    : `/${galerieSlug}/contacts?onglet=demandes&etat=a_traiter`;
  const priorite: 'reservation' | 'demande' | 'calme' = prochaineReservation
    ? 'reservation'
    : prochaineDemande
      ? 'demande'
      : 'calme';

  // L'œuvre en vedette : celle de la priorité s'il y en a une, sinon celle que
  // le serveur a choisie (§ 20.2 — vivante, avec média qualifiant ; jamais
  // `dernieres[0]` pris tel quel, qui peut désigner une œuvre archivée et
  // sans média). Un seul document est demandé, jamais une liste.
  const idVedette = voitOeuvres
    ? (prochaineReservation?.oeuvreId ?? oeuvresSection?.vedette?.id ?? null)
    : null;
  const vedette = useOeuvre(galerieSlug, idVedette);
  const oeuvreVedette = vedette.data?.oeuvre ?? null;
  const mediaVedette =
    vedette.data?.medias.find((media) => media.id === vedette.data?.oeuvre.mediaPrincipalId) ??
    vedette.data?.medias[0] ??
    null;
  const apercuVedette = useApercuMedia(galerieSlug, mediaVedette?.id ?? null);

  const indicateurs: { key: string; valeur: number; libelle: string; to: string }[] = [];
  if (voitOeuvres && oeuvresSection) {
    indicateurs.push({
      key: 'disponibles',
      valeur: oeuvresSection.parStatut.disponible,
      libelle: t('regie:tableauDeBord.indicateurs.disponibles'),
      to: `/${galerieSlug}/oeuvres`,
    });
  }
  if (voitDemandes && demandes) {
    indicateurs.push({
      key: 'demandes',
      valeur: demandes.aTraiter,
      libelle: t('regie:tableauDeBord.indicateurs.demandes'),
      to: `/${galerieSlug}/contacts?onglet=demandes&etat=a_traiter`,
    });
  }
  if (voitReservations && reservations) {
    indicateurs.push({
      key: 'reservations',
      valeur: reservations.actives,
      libelle: t('regie:tableauDeBord.indicateurs.reservationsActives'),
      to: `/${galerieSlug}/oeuvres`,
    });
  }
  if (voitEcheances && coffre) {
    indicateurs.push({
      key: 'echeances',
      valeur: coffre.enRetard + coffre.sousTrenteJours,
      libelle: t('regie:tableauDeBord.indicateurs.echeances'),
      to: `/${galerieSlug}/coffre?vue=echeances`,
    });
  }

  if (donnees?.dormantes && galerieAModule(galerie, 'pilotage')) {
    indicateurs.push({
      key: 'dormantes',
      valeur: donnees.dormantes.total,
      libelle: t('regie:pilotage.dormantes'),
      to: `/${galerieSlug}/pilotage?volet=dormantes`,
    });
  }

  // La case fantôme de « La galerie en un regard » : le chiffre d'affaires
  // facturé, tant que la facturation est à venir. Un trait gris, jamais un montant.
  const indicateurFantome = indicateurs.length > 0 && avenir.modules.includes('facturation');
  const nombreIndicateurs = indicateurs.length + (indicateurFantome ? 1 : 0);

  const blocJour: ReactNode = donneesPretes ? (
    <section
      data-ui="dashboard-day"
      className={`${styles.jour} ${voitOeuvres && idVedette ? '' : styles.jourSeul}`}
    >
      <div className={styles.jourTexte} data-ui="dashboard-priority">
        <div className={styles.jourEntete}>
          <h2>{t('regie:tableauDeBord.priorite.titre')}</h2>
        </div>

        {priorite === 'reservation' && prochaineReservation ? (
          <div className={styles.priorite}>
            <span className={`${styles.label} ${styles.etat} ${styles.etatReservation}`}>
              {t('regie:tableauDeBord.priorite.reservationLabel')}
            </span>
            <Link
              className={styles.prioriteLien}
              to={`/${galerieSlug}/oeuvres/${prochaineReservation.oeuvreId}`}
            >
              <p
                data-ui="dashboard-priority-title"
                className={`${styles.prioriteTitre} ${styles.prioriteOeuvre}`}
              >
                {prochaineReservation.oeuvreTitre}
              </p>
              <p className={styles.prioriteDetail}>
                {t('ventes:reservations.jusquau', {
                  date: formatDate(new Date(prochaineReservation.jusquau), langue, 'court'),
                })}
              </p>
            </Link>
            <div className={styles.prioriteActions}>
              <Button
                variant="secondary"
                onClick={() => navigate(`/${galerieSlug}/oeuvres/${prochaineReservation.oeuvreId}`)}
              >
                {t('regie:tableauDeBord.vedette.voir')}
              </Button>
            </div>
          </div>
        ) : priorite === 'demande' && prochaineDemande ? (
          <div className={styles.priorite}>
            <span className={`${styles.label} ${styles.etat} ${styles.etatDemande}`}>
              {t('regie:tableauDeBord.priorite.demandeLabel')}
            </span>
            <Link className={styles.prioriteLien} to={lienProchaineDemande}>
              <p className={styles.prioriteTitre} data-ui="dashboard-priority-title">
                {prochaineDemande.nom}
              </p>
              <p className={styles.prioriteDetail}>{prochaineDemande.sujet}</p>
            </Link>
            <div className={styles.prioriteActions}>
              <Button variant="secondary" onClick={() => navigate(lienProchaineDemande)}>
                {t('regie:tableauDeBord.priorite.ouvrirDemande')}
              </Button>
            </div>
          </div>
        ) : (
          <div className={styles.priorite}>
            <span className={`${styles.label} ${styles.etat} ${styles.etatCalme}`}>
              {t('regie:tableauDeBord.priorite.calmeLabel')}
            </span>
            <p className={styles.prioriteTitre} data-ui="dashboard-priority-title">
              {t('regie:tableauDeBord.priorite.calmeTitre')}
            </p>
            <p className={styles.prioriteDetail}>{t('regie:tableauDeBord.priorite.calmeTexte')}</p>
          </div>
        )}
      </div>

      {voitOeuvres && idVedette ? (
        <div className={styles.vedette} data-ui="dashboard-feature">
          {oeuvreVedette ? (
            <Link className={styles.vedetteLien} to={`/${galerieSlug}/oeuvres/${idVedette}`}>
              <div className={styles.vedetteImage} data-ui="dashboard-image">
                {apercuVedette.data ? (
                  <img src={apercuVedette.data} alt="" />
                ) : (
                  <ImageIcon
                    size={28}
                    strokeWidth={1.5}
                    className={styles.vedetteIcone}
                    aria-hidden
                  />
                )}
              </div>
              <div className={styles.vedetteLegende}>
                <div className={priorite === 'reservation' ? styles.legendeDoublon : undefined}>
                  <span className={styles.label}>{t('regie:tableauDeBord.vedette.label')}</span>
                  <strong>{oeuvreVedette.titre}</strong>
                  <small>
                    {oeuvreVedette.artisteNom}
                    {oeuvreVedette.annee ? `, ${String(oeuvreVedette.annee)}` : ''}
                  </small>
                </div>
                <span className={styles.vedetteFleche} aria-hidden="true">
                  <ArrowUpRight size={16} strokeWidth={1.5} />
                </span>
              </div>
            </Link>
          ) : null}
        </div>
      ) : null}
    </section>
  ) : null;

  const blocApercu: ReactNode =
    donneesPretes && indicateurs.length > 0 ? (
      <section
        className={styles.apercu}
        aria-label={t('regie:tableauDeBord.apercu.titre')}
        data-ui="dashboard-overview"
      >
        <div className={styles.apercuEntete}>
          <h2>{t('regie:tableauDeBord.apercu.titre')}</h2>
        </div>
        <div
          className={styles.indicateurs}
          style={{ '--indicateurs-n': nombreIndicateurs } as CSSProperties}
          data-ui="dashboard-metrics"
        >
          {indicateurs.map((indicateur) => (
            <Link
              key={indicateur.key}
              className={styles.indicateur}
              to={indicateur.to}
              data-ui="dashboard-metric"
            >
              <span className={styles.indicateurValeur} data-ui="dashboard-value">
                {indicateur.valeur}
              </span>
              <span className={styles.indicateurLibelle}>{indicateur.libelle}</span>
            </Link>
          ))}
          {indicateurFantome ? (
            <Link
              className={`${styles.indicateur} ${styles.indicateurFantome}`}
              to={`/${galerieSlug}/modules/facturation`}
              data-ui="dashboard-metric-ghost"
            >
              <span className={styles.fantomeValeur} aria-hidden="true">
                <span className={styles.fantomeTrait} />
                <Lock size={16} strokeWidth={1.5} />
              </span>
              <span className={styles.indicateurLibelle}>
                {t('regie:modulesAVenir.regard.libelle')}
                <span className={styles.fantomeNote}>
                  {t(`regie:modulesAVenir.arrive.${palierDuModule('facturation')}`)}
                </span>
              </span>
            </Link>
          ) : null}
        </div>
      </section>
    ) : null;

  const blocSuivis: ReactNode =
    donneesPretes && (voitDemandes || voitEcheances || voitExpositions) ? (
      <div className={styles.suivis} data-ui="dashboard-followups">
        {galerieAModule(galerie, 'registre-police') &&
          galerieALaction(galerie, 'registre-police:read') &&
          donnees?.registrePolice && (
            <Suivi
              entete={{
                titre: t('regie:registrePolice.propositions'),
                lien: {
                  to: `/${galerieSlug}/registre-police`,
                  libelle: t('regie:registrePolice.titre'),
                },
              }}
            >
              <p>
                {t('regie:registrePolice.compte', {
                  count: donnees.registrePolice.propositionsEnAttente,
                })}
              </p>
            </Suivi>
          )}
        {voitDemandes ? (
          <Suivi
            entete={{
              titre: (
                <>
                  <span className={styles.suiviIcone} aria-hidden="true">
                    <Contact size={14} strokeWidth={1.5} />
                  </span>
                  {t('regie:tableauDeBord.demandes.titre')}
                  {demandes && demandes.aTraiter > 0 ? (
                    <span className={styles.compteur}>{demandes.aTraiter}</span>
                  ) : null}
                </>
              ),
              lien: {
                to: `/${galerieSlug}/contacts?onglet=demandes&etat=a_traiter`,
                libelle: t('regie:tableauDeBord.demandes.voirTout'),
              },
            }}
          >
            {!demandes || demandes.dernieres.length === 0 ? (
              <p className={styles.vide}>{t('regie:tableauDeBord.demandes.vide')}</p>
            ) : (
              <ul className={styles.liste}>
                {demandes.dernieres.map((demande) => (
                  <li key={demande.id}>
                    <Link
                      className={styles.ligneDemande}
                      to={`/${galerieSlug}/contacts?onglet=demandes&etat=a_traiter#demande-${encodeURIComponent(demande.id)}`}
                    >
                      <span className={styles.ligneTitre}>{demande.nom}</span>
                      <span className={styles.ligneDetail}>{demande.sujet}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Suivi>
        ) : null}

        {voitEcheances ? (
          <Suivi
            entete={{
              titre: (
                <>
                  <span className={styles.suiviIcone} aria-hidden="true">
                    <Lock size={14} strokeWidth={1.5} />
                  </span>
                  {t('regie:tableauDeBord.echeances.titre')}
                </>
              ),
              lien: {
                to: `/${galerieSlug}/coffre?vue=echeances`,
                libelle: t('regie:tableauDeBord.echeances.voirTout'),
              },
            }}
          >
            {!coffre || coffre.prochaines.length === 0 ? (
              <p className={styles.vide}>{t('regie:tableauDeBord.echeances.vide')}</p>
            ) : (
              <ul className={styles.liste}>
                {coffre.prochaines.map((echeance) => (
                  <li key={echeance.id}>
                    <Link
                      className={styles.ligneDemande}
                      to={`/${galerieSlug}/coffre/${echeance.id}`}
                    >
                      <span className={styles.ligneTitre}>{echeance.titre}</span>
                      <Badge
                        tone={teinteDeLEcheance(echeance.joursRestants)}
                        className={echeance.joursRestants < 0 ? styles.echeanceDepassee : undefined}
                      >
                        {formatDate(new Date(echeance.echeanceLe), langue, 'court')}
                      </Badge>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Suivi>
        ) : null}

        {voitExpositions ? (
          <Suivi
            entete={{
              titre: (
                <>
                  <span className={styles.suiviIcone} aria-hidden="true">
                    <CalendarRange size={14} strokeWidth={1.5} />
                  </span>
                  {t('regie:tableauDeBord.expositions.titre')}
                </>
              ),
              lien: {
                to: `/${galerieSlug}/expositions`,
                libelle: t('regie:tableauDeBord.expositions.voirTout'),
              },
            }}
          >
            {!expositions || expositions.prochaines.length === 0 ? (
              <p className={styles.vide}>{t('regie:tableauDeBord.expositions.vide')}</p>
            ) : (
              <ul className={styles.liste}>
                {expositions.prochaines.map((exposition) => (
                  <li key={exposition.id}>
                    <Link
                      className={styles.ligneDemande}
                      to={`/${galerieSlug}/expositions/${exposition.id}`}
                    >
                      <span className={styles.ligneTitre}>
                        {texteEnLangue(exposition.titre, langue)}
                      </span>
                      <Badge tone={teinteDeLEtat(exposition.etat)}>
                        {t(`regie:expositions.etats.${exposition.etat}`)}
                      </Badge>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Suivi>
        ) : null}
      </div>
    ) : null;

  const blocIntrouvables: ReactNode = donneesPretes &&
    donnees?.introuvables &&
    galerieAModule(galerie, 'reserve-inventaire') &&
    galerieALaction(galerie, 'inventaire:read') && (
      <Suivi entete={{ titre: t('regie:inventaire.oeuvresIntrouvables') }}>
        <p>{donnees.introuvables.total}</p>
        <ul className={styles.liste}>
          {donnees.introuvables.dernieres.map((o) => (
            <li key={o.id}>
              <Link className={styles.ligneDemande} to={`/${galerieSlug}/oeuvres/${o.oeuvreId}`}>
                <span>{o.titre}</span>
                <span>{o.note}</span>
              </Link>
            </li>
          ))}
        </ul>
      </Suivi>
    );

  const blocSite: ReactNode =
    donneesPretes && voitSite ? (
      <section
        data-ui="dashboard-site"
        className={`${styles.site} ${derniere?.etat === 'echouee' ? styles.siteAlerte : ''}`}
        aria-label={t('regie:tableauDeBord.site.titre')}
      >
        <span className={styles.siteMarque} aria-hidden="true">
          <Globe size={18} strokeWidth={1.5} />
        </span>
        <div className={styles.siteTexte}>
          <span className={styles.siteLabel}>{t('regie:tableauDeBord.site.titre')}</span>
          <p className={styles.siteTitre} role={derniere?.etat === 'echouee' ? 'alert' : undefined}>
            {derniere === null
              ? t('site:publication.jamais')
              : derniere.etat === 'en_cours'
                ? t('site:publication.enCours')
                : derniere.etat === 'echouee'
                  ? t('regie:tableauDeBord.site.echec')
                  : t('site:publication.derniere', {
                      date: formatDate(new Date(derniere.date), langue, 'court'),
                    })}
          </p>
        </div>
        {donnees?.site?.oeuvresIncompletes !== undefined ? (
          <Link to={`/${galerieSlug}/oeuvres?incompletes=site&visibleSurSite=true`}>
            {t('regie:tableauDeBord.site.incompletes', { count: donnees.site.oeuvresIncompletes })}
          </Link>
        ) : null}
        <div className={styles.siteActions}>
          <Link className={classesBouton('secondary', 'sm')} to={`/${galerieSlug}/site`}>
            {t('regie:tableauDeBord.site.voir')}
          </Link>
          {peutPublier && derniere?.etat !== 'en_cours' ? (
            <Button
              variant="secondary"
              disabled={publier.isPending}
              onClick={() => publier.mutate(undefined)}
            >
              {t('site:publication.publier')}
            </Button>
          ) : null}
        </div>
        {publier.isSuccess ? (
          <p className={styles.confirmation}>{t('regie:tableauDeBord.site.publicationLancee')}</p>
        ) : null}
      </section>
    ) : null;

  // Les blocs réordonnables. Chacun vaut `null` quand il n'a rien à montrer
  // (droits, données absentes, chargement) : il n'apparaît alors ni à l'écran,
  // ni dans la liste des déplacements.
  const voitPrevision =
    galerieAModule(galerie, 'pipeline-ventes') && galerieALaction(galerie, 'pipeline:read');
  // Mêmes requêtes que `Prevision` et `BlocAPrevenir` (cache partagé) : elles
  // décident ici si le bloc existe, pour ne jamais laisser un cadre vide.
  const prevision = usePrevision(galerieSlug, 'en_cours', voitPrevision);
  const previsionPresente =
    voitPrevision &&
    !prevision.isError &&
    typeof prevision.data?.realiseCentimes === 'number' &&
    typeof prevision.data.pondereCentimes === 'number' &&
    typeof prevision.data.sansDateCentimes === 'number';
  const voitAPrevenir =
    galerieAModule(galerie, 'profils-collectionneurs') && galerieALaction(galerie, 'profils:read');
  const profils = useTableauProfils(galerieSlug, voitAPrevenir);
  const aPrevenirPresent = voitAPrevenir && (profils.data?.oeuvres.length ?? 0) > 0;

  const contenus: Record<IdModule, ReactNode> = {
    prevision: previsionPresente ? <Prevision /> : null,
    jour: blocJour,
    apercu: blocApercu,
    aprevenir: aPrevenirPresent ? <BlocAPrevenir /> : null,
    suivis: blocSuivis,
    introuvables: blocIntrouvables,
    site: blocSite,
  };
  const [ordreComplet, changerOrdre] = useOrdreModules(compte.id, galerieSlug);
  const blocsAffiches: BlocAffiche[] = ordreComplet
    .filter((id) => contenus[id])
    .map((id) => ({
      id,
      libelle: t(`regie:tableauDeBord.personnaliser.modules.${id}`),
      contenu: contenus[id],
    }));
  // Le mode « Personnaliser » : explicite, pour qu'aucun glissement ne soit involontaire.
  const [edition, setEdition] = useState(false);
  const peutPersonnaliser = blocsAffiches.length > 1;

  return (
    <div className={styles.page} data-ui="dashboard">
      <header className={styles.entete} data-ui="page-heading">
        <p className={styles.date}>{formatDate(aujourdhui, langue, 'long')}</p>
        <h1 className={styles.titre} data-ui="page-title">
          {t('regie:tableauDeBord.titre')}
        </h1>
        <p className={styles.salutation}>
          {t('regie:tableauDeBord.salutation', { nom: compte.nom })}{' '}
          {t('regie:tableauDeBord.sousTitre')}
        </p>
        {peutPersonnaliser && !edition ? (
          <Button
            variant="ghost"
            size="sm"
            className={styles.personnaliser}
            onClick={() => setEdition(true)}
          >
            <SlidersHorizontal size={14} strokeWidth={1.5} aria-hidden="true" />
            {t('regie:tableauDeBord.personnaliser.ouvrir')}
          </Button>
        ) : null}
      </header>

      {tableau.isLoading ? (
        <p className={styles.chargement}>{t('regie:commun.chargement')}</p>
      ) : tableau.isError ? (
        <p className={styles.erreurGlobale} role="alert">
          {messageErreur}
        </p>
      ) : null}

      <BlocsReorganisables
        blocs={blocsAffiches}
        edition={edition}
        onOrdre={(ordre) => changerOrdre(appliquerOrdreVisible(ordreComplet, ordre))}
        onReinitialiser={() => changerOrdre(null)}
        onTermine={() => setEdition(false)}
      />

      {avenir.aucun ? null : (
        <>
          <BientotSection
            galerieSlug={galerieSlug}
            compteId={compte.id}
            avenir={avenir}
            onVoirSurLaCarte={voirSurLaCarte}
          />
          <CarteDesModules
            ref={titreCarte}
            galerie={galerie}
            galerieSlug={galerieSlug}
            avenir={avenir}
            filtre={filtreCarte}
            onFiltre={setFiltreCarte}
          />
        </>
      )}
    </div>
  );
}
